import React, { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import "react-toastify/dist/ReactToastify.min.css";
import { useRecoilState } from "recoil";
import { authState } from "./atoms/authState";
import ProtectedRoute from "./components/ProtectedRoute";
import { getUserFromStorage } from "./utils/auth";
import GlobalMessage from "./components/common/GlobalMessage";
import GlobalConfirm from "@components/GlobalConfirm";
import SpaceGuard from "./components/SpaceGuard";
import NotFound from "./components/NotFound";
import PageLoader from "./components/PageLoader";
import AppErrorBoundary from "./components/AppErrorBoundary";
import { setupImpersonationAxios } from "./utils/impersonationAxiosSetup";
import { useCashMovementsInit } from "./hooks/useCashMovementsInit";

const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const ImpersonatePage = lazy(() => import("@pages/auth/ImpersonatePage"));
const DashboardLayout = lazy(() => import("./components/DashboardLayout"));
const PoolRoutes = lazy(() => import("./Routes/PoolRoutes"));
const CompanyRoutes = lazy(() => import("./Routes/CompanyRoutes"));
const IntermediaireRoutes = lazy(() => import("./Routes/IntermediaireRoutes"));
const AgencyRoutes = lazy(() => import("./Routes/AgencyRoutes"));
const IntermediaireAgencyRoutes = lazy(() => import("./Routes/IntermediaireAgencyRoutes"));

function App() {
  const [authValue, setAuthValue] = useRecoilState(authState);
  
  // Initialize cash movements status check
  useCashMovementsInit();

  useEffect(() => {
    let userData = getUserFromStorage();
    if (!userData) {
      setAuthValue(null);
    }
    setAuthValue(userData);

    setupImpersonationAxios();

    return () => {};
  }, [setAuthValue]);

  return (
    <>
      <BrowserRouter>
        <GlobalMessage />
        <GlobalConfirm />
        <AppErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route
                path="/"
                element={
                  <ProtectedRoute authValue={authValue}>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route
                  path="pool/*"
                  element={
                    <SpaceGuard allowedSpace="pool">
                      <PoolRoutes />
                    </SpaceGuard>
                  }
                />
                <Route
                  path="company/*"
                  element={
                    <SpaceGuard allowedSpace="company">
                      <CompanyRoutes />
                    </SpaceGuard>
                  }
                />
                <Route
                  path="agency/*"
                  element={
                    <SpaceGuard allowedSpace="agency">
                      <AgencyRoutes />
                    </SpaceGuard>
                  }
                />
                <Route
                  path="intermediaire/*"
                  element={
                    <SpaceGuard allowedSpace="intermediaire">
                      <IntermediaireRoutes />
                    </SpaceGuard>
                  }
                />
                <Route
                  path="intermediaire-agency/*"
                  element={
                    <SpaceGuard allowedSpace="intermediaire-agency">
                      <IntermediaireAgencyRoutes />
                    </SpaceGuard>
                  }
                />
                {/* Route catch-all pour les URLs invalides */}
                <Route path="*" element={<NotFound />} />
              </Route>
              <Route path="connexion" element={<LoginPage />} />
              <Route path="auth/impersonate" element={<ImpersonatePage />} />
            </Routes>
          </Suspense>
        </AppErrorBoundary>
      </BrowserRouter>
      <ToastContainer />
    </>
  );
}

export default App;
